import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { KpiCard, Badge, Panel } from "@/components/kit";
import api from "@/lib/api";
import { fmtN, fmtPct, fmtInt, arrow, C } from "./qUtils";

export const SortTh = ({ k, label, sort, setSort, right = true }) => (
  <th className={`cursor-pointer select-none ${right ? "text-right" : ""}`} onClick={() => setSort((s) => ({ k, dir: s.k === k && s.dir === "desc" ? "asc" : "desc" }))}>
    {label} {sort.k === k && <span className="text-thcyan">{sort.dir === "desc" ? "↓" : "↑"}</span>}
  </th>
);
export const sortRows = (rows, sort) => [...rows].sort((a, b) => {
  const av = a[sort.k], bv = b[sort.k];
  if (av == null) return 1;
  if (bv == null) return -1;
  return (typeof av === "string" ? av.localeCompare(bv) : av - bv) * (sort.dir === "desc" ? -1 : 1);
});

export const ProgressBar = ({ value, color, max = 100 }) => (
  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-thhover/8"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, Math.max(0, (100 * (value || 0)) / max))}%`, background: color }} /></div>
);

export const Kpi = ({ testid, label, value, sub, color, bar, badge, delay }) => (
  <KpiCard testid={testid} label={label} value={value} sub={sub} color={color} badge={badge} delay={delay}>{bar != null && <ProgressBar value={bar} color={color} />}</KpiCard>
);

export const HBars = ({ testid, title, rows, color, fmt = (v) => fmtPct(v), right, empty = "No data" }) => (
  <Panel testid={testid} title={title} right={right}>
    {rows.length === 0 ? <p className="text-sm text-thdim">{empty}</p> : (
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_auto] items-center gap-2 text-xs" data-testid={`${testid}-row`} onClick={r.onClick} role={r.onClick ? "button" : undefined}>
            <span className={`truncate font-semibold text-thstrong ${r.onClick ? "cursor-pointer hover:text-thamber" : ""}`} title={r.title || r.label}>{r.label}</span>
            <div className="h-2 overflow-hidden rounded-full bg-thhover/8"><div className="h-full rounded-full" style={{ width: `${Math.min(100, r.value || 0)}%`, background: color(r.value) }} /></div>
            <span className="w-24 text-right font-mono-hud">{fmt(r.value)}{r.n != null && <span className="text-thdim"> · {fmtInt(r.n)}</span>}</span>
          </div>
        ))}
      </div>
    )}
  </Panel>
);

export const Diff = ({ a, b, lowerBetter = false, neutral = false, fmt = (v) => fmtN(v), testid }) => {
  if (a == null || b == null) return <span className="font-mono-hud text-thdim">—</span>;
  const d = b - a; const ar = neutral ? { sym: d > 0 ? "▲" : d < 0 ? "▼" : "•", color: C.grey } : arrow(d, lowerBetter);
  return <span data-testid={testid} className="whitespace-nowrap font-mono-hud font-semibold" style={{ color: ar.color }}>{ar.sym} {d > 0 ? "+" : ""}{fmt(d)}</span>;
};

export const MixBar = ({ mix, testid }) => {
  const total = mix.S + mix.NC + mix.C || 1;
  return (
    <div data-testid={testid} className="flex h-2 w-full overflow-hidden rounded-full bg-thhover/8">
      {[["S", C.mint], ["NC", C.amber], ["C", C.coral]].map(([k, c]) => <div key={k} style={{ width: `${(100 * mix[k]) / total}%`, background: c }} />)}
    </div>
  );
};

export const Tag = ({ children, tone = "warn", onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`rounded-full px-2 py-0.5 font-mono-hud text-[10px] transition-colors ${tone === "bad" ? "bg-thcoral/15 text-thcoral hover:bg-thcoral/25" : "bg-thamber/15 text-thamber hover:bg-thamber/25"}`}>{children}</button>
);

const Q_COLOR = ["#2F6FD6", "#1F9D6B", "#8A4FD1", "#D4620F"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MonthGrid = ({ months, value, onChange, testid, min = 2, title = "Select months to compare (2 or more)", fallback = null }) => {
  const years = [...new Set(months.map((m) => m.slice(0, 4)))].sort();
  const avail = new Set(months);
  const sel = value.filter((m) => avail.has(m)).sort();
  const toggle = (m) => onChange(sel.includes(m) ? sel.filter((x) => x !== m) : [...sel, m].sort());
  return (
    <Panel testid={testid} title={title} right={<div className="flex gap-1"><button type="button" data-testid={`${testid}-all`} onClick={() => onChange([...months].sort())} className="mk-btn-ghost !px-2.5 !py-1 !text-[10px]">Select all</button><button type="button" data-testid={`${testid}-clear`} onClick={() => onChange([])} className="mk-btn-ghost !px-2.5 !py-1 !text-[10px]">Clear</button></div>}>
      <div className="space-y-2">{years.map((y) => (
        <div key={y} className="grid grid-cols-3 gap-1.5 sm:grid-cols-6 xl:grid-cols-12" data-testid={`${testid}-year-${y}`}>{MON.map((name, i) => {
          const m = `${y}-${String(i + 1).padStart(2, "0")}`, q = Math.floor(i / 3), c = Q_COLOR[q], has = avail.has(m), on = sel.includes(m);
          return <button key={m} type="button" data-testid={`${testid}-${m}`} data-state={on ? "on" : has ? "off" : "empty"} disabled={!has} onClick={() => toggle(m)} title={has ? `${name} ${y}` : `${name} ${y} · no data uploaded`}
            className={`relative rounded-lg px-1 py-2 text-center transition-colors ${on ? "border-2" : "border border-dashed border-thborder/30 bg-thhover/5"} ${has ? "hover:brightness-110" : "cursor-not-allowed opacity-40"}`} style={on ? { borderColor: c, background: `${c}1f`, color: c } : { color: "rgb(var(--th-mute))" }}>
            {on && <span className="absolute right-1 top-0.5 text-[10px] font-bold" style={{ color: c }}>✓</span>}
            <span className="block text-sm font-bold" style={on ? { color: c } : undefined}>{name}</span><span className="block font-mono-hud text-[9px] opacity-80">{m}</span></button>;
        })}</div>))}</div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] text-thmute" data-testid={`${testid}-legend`}>{["Q1 Jan–Mar", "Q2 Apr–Jun", "Q3 Jul–Sep", "Q4 Oct–Dec"].map((l, i) => <span key={l} className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: Q_COLOR[i] }} />{l}</span>)}<span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-thborder/40 bg-thhover/10" />Not selected</span><span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-thborder/20 opacity-40" />No data</span></div>
      <p className="mt-2 text-xs" data-testid={`${testid}-live`}>{sel.length ? <span className="text-thbody">{sel.length} month{sel.length > 1 ? "s" : ""} selected: {sel.map((m) => MON[Number(m.slice(5, 7)) - 1]).join(", ")}</span> : <span className="text-thdim">No months selected</span>}{sel.length < min && <span className="ml-2 font-semibold text-thcoral" data-testid={`${testid}-warn`}>Select at least {min} month{min > 1 ? "s" : ""} to compare.{fallback ? ` Showing ${fallback} until then.` : ""}</span>}</p>
    </Panel>
  );
};

const hashStr = (s) => { let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); };
const shortKey = (k) => (k.length <= 180 ? k : `${k.slice(0, 60)}#${hashStr(k)}`);

export function AiSummary({ tenantId, k, kind, facts, bullets = [], testid = "q-ai", title = "Detailed summary", endpoint = "/modules/quality/narrative" }) {
  const [state, setState] = useState({ text: null, loading: false, fallback: false });
  const sig = JSON.stringify(facts);
  const last = useRef("");
  const run = (regenerate = false) => {
    if (!tenantId || !facts || Object.keys(facts).length === 0) return;
    const mine = sig;
    setState({ text: null, loading: true, fallback: false });
    api.post(endpoint, { tenant_id: tenantId, key: shortKey(k), kind, facts, regenerate })
      .then((r) => { if (last.current === mine) setState({ text: r.data.text, loading: false, fallback: !!r.data.fallback }); })
      .catch(() => { if (last.current === mine) setState({ text: null, loading: false, fallback: true }); });
  };
  useEffect(() => { if (sig !== last.current) { last.current = sig; run(false); } }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Panel testid={testid} title={title} right={
      <div className="flex items-center gap-2">
        <Badge tone="info"><Sparkles className="h-3 w-3" /> AI</Badge>
        <button data-testid={`${testid}-regenerate`} onClick={() => run(true)} disabled={state.loading} className="mk-btn-ghost !px-2 !py-1 !text-[10px]" title="Regenerate">
          {state.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </button>
      </div>}>
      {bullets.length > 0 && (
        <ul className="mb-3 grid gap-1 text-xs text-thbody sm:grid-cols-2" data-testid={`${testid}-pointers`}>
          {bullets.filter(Boolean).map((b, i) => <li key={i} className="flex gap-2"><span className="text-thamber">›</span><span>{b}</span></li>)}
        </ul>
      )}
      {state.text ? <p data-testid={`${testid}-text`} className="text-sm leading-relaxed text-thstrong">{state.text}</p>
        : state.loading ? <p className="mk-sub">Writing summary from the computed figures…</p>
        : state.fallback ? <p className="mk-sub" data-testid={`${testid}-fallback`}>AI narrative unavailable — computed pointers shown above.</p> : null}
    </Panel>
  );
}
